/**
 * Super Admin - Properties Management
 * Dedicated page for managing properties
 */

import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";

// @mui material components
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Chip from "@mui/material/Chip";
import IconButton from "@mui/material/IconButton";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import TextField from "@mui/material/TextField";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import CircularProgress from "@mui/material/CircularProgress";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";

// Material Dashboard 2 React examples
import DataTable from "examples/Tables/DataTable";
import ComplexStatisticsCard from "examples/Cards/StatisticsCards/ComplexStatisticsCard";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";

// API & constants
import { superAdminAPI } from "utils/api";
import { PROPERTY_STATUS_OPTIONS } from "constants/propertyStatusOptions";

function PropertiesManagement() {
  const navigate = useNavigate();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [menuAnchorPosition, setMenuAnchorPosition] = useState(null);
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [actionDialog, setActionDialog] = useState({ open: false, action: null });
  const [rejectReason, setRejectReason] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [readyForInspectorFilter, setReadyForInspectorFilter] = useState("all");
  const [meetingRequestedFilter, setMeetingRequestedFilter] = useState("all");
  const [submitting, setSubmitting] = useState(false);
  const [stats, setStats] = useState({
    total: 0,
    readyForInspection: 0,
    notReadyForInspection: 0,
    byStatus: {},
  });

  useEffect(() => {
    fetchProperties();
  }, [statusFilter, readyForInspectorFilter]);

  const fetchProperties = async () => {
    try {
      setLoading(true);
      const params = { limit: 10000 };
      if (statusFilter !== "all") params.status = statusFilter;
      if (readyForInspectorFilter === "true") params.readyForInspector = "true";
      if (readyForInspectorFilter === "false") params.readyForInspector = "false";
      const response = await superAdminAPI.getAllProperties(params);
      const list = response.data || response.properties || [];
      setData(list);
      // Stats are computed from displayData (see useMemo below)
    } catch (error) {
      console.error("Error fetching properties:", error);
    } finally {
      setLoading(false);
    }
  };

  // Client-side filter for contract response (only in contracting phase: Inspection_Offer or Contracting)
  const displayData = useMemo(() => {
    if (meetingRequestedFilter === "all") return data;
    return data.filter((p) => {
      const inContractPhase = p.status === "Inspection_Offer" || p.status === "Contracting";
      return inContractPhase && p.contractResponse === meetingRequestedFilter;
    });
  }, [data, meetingRequestedFilter]);

  // Compute stats from displayed list so they update with filters
  useEffect(() => {
    const total = displayData.length;
    const readyForInspection = displayData.filter((p) => p.readyForInspector === true).length;
    const byStatus = {};
    PROPERTY_STATUS_OPTIONS.forEach(({ value }) => {
      byStatus[value] = displayData.filter((p) => p.status === value).length;
    });
    setStats({
      total,
      readyForInspection,
      notReadyForInspection: total - readyForInspection,
      byStatus,
    });
  }, [displayData]);

  const handleAction = async (propertyId, action, reason = "", currentProperty = null) => {
    try {
      setSubmitting(true);
      let response;

      switch (action) {
        case "approve":
          // Inspection_Completed: approve to Inspection Offer (draft contract + email with PDF)
          if (currentProperty?.status === "Inspection_Completed") {
            response = await superAdminAPI.approveToInspectionOffer(propertyId);
          } else {
            response = await superAdminAPI.approveProperty(propertyId);
          }
          break;
        case "reject":
          if (!reason || reason.trim().length === 0) {
            alert("Rejection reason is required");
            setSubmitting(false);
            return;
          }
          response = await superAdminAPI.rejectProperty(propertyId, reason);
          break;
        case "suspend":
          response = await superAdminAPI.suspendProperty(propertyId, reason);
          break;
        case "maintenance":
          response = await superAdminAPI.setPropertyMaintenance(propertyId, reason);
          break;
        case "delete":
          response = await superAdminAPI.deleteProperty(propertyId);
          break;
        default:
          setSubmitting(false);
          return;
      }

      // Check if response is successful (either response.success or just a successful response)
      if (response && response.success !== false) {
        // Refresh the properties list
        await fetchProperties();
        // Close dialog and reset state
        setActionDialog({ open: false, action: null });
        setRejectReason("");
        setSelectedProperty(null);
        setMenuAnchorPosition(null);
        // Show success message
        const isApproveToInspectionOffer =
          action === "approve" && currentProperty?.status === "Inspection_Completed";
        let successMsg;
        if (isApproveToInspectionOffer) {
          successMsg =
            "Draft contract has been sent to the property owner. Waiting for their response (Approve / Decline / Request meeting).";
        } else {
          const actionMessages = {
            delete: "deleted",
            suspend: "suspended",
            maintenance: "set to maintenance",
            approve: "approved",
            reject: "rejected",
          };
          successMsg = `Property ${actionMessages[action] || action + "d"} successfully!`;
        }
        alert(successMsg);
      } else {
        throw new Error(response?.message || `Failed to ${action} property`);
      }
    } catch (error) {
      console.error(`Error ${action}ing property:`, error);
      alert(`Failed to ${action} property: ${error.message || error}`);
    } finally {
      setSubmitting(false);
    }
  };

  const openMenu = (event, property) => {
    if (event?.currentTarget) {
      const rect = event.currentTarget.getBoundingClientRect();
      setMenuAnchorPosition({ top: rect.bottom, left: rect.right });
      setSelectedProperty(property);
    }
  };

  const closeMenu = () => {
    setMenuAnchorPosition(null);
    // Don't clear selectedProperty here - it's needed for the dialog
  };

  const openActionDialog = (action) => {
    setActionDialog({ open: true, action });
    setMenuAnchorPosition(null); // Close menu but keep selectedProperty
  };

  const confirmAction = () => {
    if (!selectedProperty || !actionDialog.action) {
      console.error("Missing selectedProperty or actionDialog.action");
      return;
    }

    const propertyId = selectedProperty._id || selectedProperty.id;
    if (!propertyId) {
      alert("Property ID is missing. Please try again.");
      return;
    }

    // Validate required fields based on action
    if (actionDialog.action === "reject" && !rejectReason.trim()) {
      alert("Please provide a reason for rejection");
      return;
    }

    // For suspend, maintenance, and delete, reason is optional
    // Call handleAction with the property ID, action, reason (if provided), and selectedProperty for status-based routing
    handleAction(propertyId, actionDialog.action, rejectReason || "", selectedProperty);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "Approved":
        return "success";
      case "Pending":
        return "warning";
      case "Inspection":
        return "info";
      case "Inspection_Completed":
        return "primary";
      case "Rejected":
        return "error";
      case "Suspended":
        return "warning";
      case "Maintenance":
        return "info";
      case "Disabled":
        return "default";
      default:
        return "info";
    }
  };

  const columns = [
    { Header: "Property ID", accessor: "shortId", width: "12%" },
    { Header: "Title", accessor: "title", width: "20%" },
    { Header: "Type", accessor: "propertyType", width: "10%" },
    { Header: "Location", accessor: "location", width: "18%" },
    {
      Header: "Status",
      accessor: "status",
      width: "15%",
      Cell: ({ value }) => (
        <Chip label={value?.replace(/_/g, " ")} color={getStatusColor(value)} size="small" />
      ),
    },
    {
      Header: "Featured",
      accessor: "featured",
      width: "10%",
      Cell: ({ value }) => (
        <Chip label={value ? "Yes" : "No"} color={value ? "success" : "default"} size="small" />
      ),
    },
    {
      Header: "Price",
      accessor: "price",
      width: "10%",
      Cell: ({ value }) => (
        <MDTypography variant="body2" fontWeight="medium">
          {value ? `EGP ${value.toLocaleString()}` : "N/A"}
        </MDTypography>
      ),
    },
    {
      Header: "Actions",
      accessor: "actions",
      width: "8%",
      Cell: ({ row }) => {
        const handleClick = (e) => openMenu(e, row.original);
        return (
          <IconButton onClick={handleClick} size="small">
            <Icon>more_vert</Icon>
          </IconButton>
        );
      },
    },
  ];

  const rows = displayData.map((property) => {
    // Extract multilingual fields
    const title =
      typeof property.title === "object"
        ? property.title?.en || property.title?.ar || "N/A"
        : property.title || "N/A";

    const city =
      typeof property.city === "object"
        ? property.city?.en || property.city?.ar || ""
        : property.city || "";

    const country =
      typeof property.country === "object"
        ? property.country?.en || property.country?.ar || ""
        : property.country || "";

    const address =
      typeof property.address === "object"
        ? property.address?.en || property.address?.ar || ""
        : property.address || "";

    const location = city && country ? `${city}, ${country}` : city || country || address || "N/A";

    return {
      shortId: property.shortId ? `ID: ${property.shortId}` : "N/A",
      title,
      propertyType: property.propertyType
        ? property.propertyType.charAt(0).toUpperCase() + property.propertyType.slice(1)
        : "N/A",
      location,
      status: property.status,
      featured: property.featured || false,
      price: property.price,
      _id: property._id || property.id,
    };
  });

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={3} px={{ xs: 2, sm: 3 }}>
        <MDBox
          mb={3}
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          flexWrap="wrap"
          gap={2}
        >
          <MDBox>
            <MDTypography variant="h4" fontWeight="bold">
              Properties Management
            </MDTypography>
            <MDTypography variant="body2" color="text">
              View all properties, set prices, approve/reject properties, and manage listings
            </MDTypography>
          </MDBox>
        </MDBox>

        {/* Statistics Section */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <MDBox width="100%" height="100%">
              <ComplexStatisticsCard
                color="dark"
                icon="home"
                title="Total"
                count={stats.total}
                percentage={{
                  color: "success",
                  amount: stats.total > 0 ? "100%" : "0%",
                  label: "of filtered",
                }}
                sx={{ width: "100%", height: "100%", minHeight: "140px" }}
              />
            </MDBox>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <MDBox width="100%" height="100%">
              <ComplexStatisticsCard
                color="success"
                icon="visibility"
                title="Ready for Inspection"
                count={stats.readyForInspection}
                percentage={{
                  color: "success",
                  amount:
                    stats.total > 0
                      ? `${Math.round((stats.readyForInspection / stats.total) * 100)}%`
                      : "0%",
                  label: "Yes",
                }}
                sx={{ width: "100%", height: "100%", minHeight: "140px" }}
              />
            </MDBox>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <MDBox width="100%" height="100%">
              <ComplexStatisticsCard
                color="secondary"
                icon="visibility_off"
                title="Not Ready for Inspection"
                count={stats.notReadyForInspection}
                percentage={{
                  color: "secondary",
                  amount:
                    stats.total > 0
                      ? `${Math.round((stats.notReadyForInspection / stats.total) * 100)}%`
                      : "0%",
                  label: "No",
                }}
                sx={{ width: "100%", height: "100%", minHeight: "140px" }}
              />
            </MDBox>
          </Grid>
        </Grid>

        {/* Per workflow step counts */}
        <Card sx={{ mb: 2 }}>
          <MDBox p={2}>
            <MDTypography
              variant="button"
              fontWeight="medium"
              color="text"
              sx={{ mb: 1.5, display: "block" }}
            >
              By workflow step
            </MDTypography>
            <MDBox display="flex" flexWrap="wrap" gap={1}>
              {PROPERTY_STATUS_OPTIONS.map(({ value, label }) => {
                const count = stats.byStatus?.[value] ?? 0;
                if (count === 0) return null;
                const pct = stats.total > 0 ? Math.round((count / stats.total) * 100) : 0;
                return (
                  <Chip
                    key={value}
                    label={`${label}: ${count} (${pct}%)`}
                    size="small"
                    color="info"
                    variant="outlined"
                    sx={{ fontWeight: 500 }}
                  />
                );
              })}
              {(!stats.byStatus || Object.values(stats.byStatus).every((c) => c === 0)) && (
                <MDTypography variant="caption" color="text.secondary">
                  No properties in current filter
                </MDTypography>
              )}
            </MDBox>
          </MDBox>
        </Card>

        {/* Status and Ready for Inspection Filters */}
        <Card sx={{ mb: 3 }}>
          <MDBox p={2}>
            <MDBox display="flex" gap={2} flexWrap="wrap" alignItems="center">
              <MDTypography variant="body2" fontWeight="medium" color="text" sx={{ mr: 1 }}>
                Filters:
              </MDTypography>
              <FormControl size="small" sx={{ minWidth: 220 }}>
                <InputLabel>Status / Workflow step</InputLabel>
                <Select
                  value={statusFilter}
                  label="Status / Workflow step"
                  onChange={(e) => setStatusFilter(e.target.value)}
                >
                  <MenuItem value="all">All</MenuItem>
                  {PROPERTY_STATUS_OPTIONS.map(({ value, label }) => (
                    <MenuItem key={value} value={value}>
                      {label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 180 }}>
                <InputLabel>Ready for Inspection</InputLabel>
                <Select
                  value={readyForInspectorFilter}
                  label="Ready for Inspection"
                  onChange={(e) => setReadyForInspectorFilter(e.target.value)}
                >
                  <MenuItem value="all">All</MenuItem>
                  <MenuItem value="true">Yes</MenuItem>
                  <MenuItem value="false">No</MenuItem>
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 200 }}>
                <InputLabel>Contract response</InputLabel>
                <Select
                  value={meetingRequestedFilter}
                  label="Contract response"
                  onChange={(e) => setMeetingRequestedFilter(e.target.value)}
                >
                  <MenuItem value="all">All</MenuItem>
                  <MenuItem value="approved">Approved</MenuItem>
                  <MenuItem value="meeting_requested">Meeting requested</MenuItem>
                  <MenuItem value="rejected">Declined</MenuItem>
                </Select>
              </FormControl>
              <Chip
                label="Clear filters"
                onClick={() => {
                  setStatusFilter("all");
                  setReadyForInspectorFilter("all");
                  setMeetingRequestedFilter("all");
                }}
                color="default"
                variant="outlined"
                sx={{ cursor: "pointer" }}
              />
            </MDBox>
          </MDBox>
        </Card>

        {/* Properties Table */}
        <Card>
          {loading ? (
            <MDBox p={6} display="flex" justifyContent="center">
              <CircularProgress />
            </MDBox>
          ) : (
            <DataTable
              table={{ columns, rows }}
              showTotalEntries={true}
              isSorted={false}
              noEndBorder
              entriesPerPage={{ defaultValue: 10, entries: [5, 10, 15, 20, 25] }}
              canSearch
            />
          )}
        </Card>

        {/* Action Menu */}
        <Menu
          id="property-menu"
          anchorReference="anchorPosition"
          anchorPosition={menuAnchorPosition ?? undefined}
          anchorEl={null}
          open={Boolean(menuAnchorPosition)}
          onClose={closeMenu}
          anchorOrigin={{
            vertical: "top",
            horizontal: "right",
          }}
          transformOrigin={{
            vertical: "top",
            horizontal: "right",
          }}
        >
          {selectedProperty?.status === "Inspection_Completed" && (
            <>
              <MenuItem onClick={() => openActionDialog("approve")}>
                <Icon sx={{ mr: 1 }}>check</Icon> Approve
              </MenuItem>
              <MenuItem onClick={() => openActionDialog("reject")}>
                <Icon sx={{ mr: 1 }}>close</Icon> Reject
              </MenuItem>
            </>
          )}
          {selectedProperty?.status === "Active" && (
            <>
              <MenuItem onClick={() => openActionDialog("suspend")}>
                <Icon sx={{ mr: 1 }}>block</Icon> Suspend
              </MenuItem>
              <MenuItem onClick={() => openActionDialog("maintenance")}>
                <Icon sx={{ mr: 1 }}>build</Icon> Set Maintenance
              </MenuItem>
            </>
          )}
          <MenuItem onClick={() => openActionDialog("delete")} sx={{ color: "error.main" }}>
            <Icon sx={{ mr: 1 }}>delete</Icon> Delete Property
          </MenuItem>
          <MenuItem
            onClick={() => {
              closeMenu();
              navigate(`/admin/properties/${selectedProperty?._id || selectedProperty?.id}`);
            }}
          >
            <Icon sx={{ mr: 1 }}>visibility</Icon> View Details
          </MenuItem>
        </Menu>

        {/* Action Confirmation Dialog */}
        <Dialog
          open={actionDialog.open}
          onClose={() => {
            if (!submitting) {
              setActionDialog({ open: false, action: null });
              setRejectReason("");
              setSelectedProperty(null);
            }
          }}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            {actionDialog.action === "approve"
              ? "Approve Property"
              : actionDialog.action === "reject"
              ? "Reject Property"
              : actionDialog.action === "suspend"
              ? "Suspend Property"
              : actionDialog.action === "maintenance"
              ? "Set Property to Maintenance"
              : actionDialog.action === "delete"
              ? "Delete Property"
              : "Confirm Action"}
          </DialogTitle>
          <DialogContent>
            <MDBox display="flex" flexDirection="column" gap={2} pt={1}>
              <MDTypography variant="body2" color="text">
                {actionDialog.action === "approve"
                  ? "Are you sure you want to approve this property? It will be published and available for booking."
                  : actionDialog.action === "reject"
                  ? "Are you sure you want to reject this property? Please provide a reason."
                  : actionDialog.action === "suspend"
                  ? "Are you sure you want to suspend this property? It will be temporarily removed from listings. You can optionally provide a reason."
                  : actionDialog.action === "maintenance"
                  ? "Are you sure you want to set this property to maintenance mode? It will be temporarily unavailable for bookings. You can optionally provide a reason."
                  : actionDialog.action === "delete"
                  ? "Are you sure you want to delete this property? This action cannot be undone and the property will be permanently removed from the system."
                  : "Are you sure you want to perform this action?"}
              </MDTypography>
              {selectedProperty && (
                <MDBox p={2} borderRadius="lg" bgColor="grey.100">
                  <MDTypography variant="subtitle2" fontWeight="medium">
                    {typeof selectedProperty.title === "object"
                      ? selectedProperty.title?.en || selectedProperty.title?.ar || "N/A"
                      : selectedProperty.title || "N/A"}
                  </MDTypography>
                  <MDTypography variant="body2" color="text">
                    {typeof selectedProperty.address === "object"
                      ? selectedProperty.address?.en || selectedProperty.address?.ar || "N/A"
                      : selectedProperty.address || "N/A"}
                  </MDTypography>
                </MDBox>
              )}
              {(actionDialog.action === "reject" ||
                actionDialog.action === "suspend" ||
                actionDialog.action === "maintenance") && (
                <TextField
                  multiline
                  rows={4}
                  fullWidth
                  label={
                    actionDialog.action === "reject"
                      ? "Rejection Reason *"
                      : actionDialog.action === "suspend"
                      ? "Suspension Reason (Optional)"
                      : "Maintenance Reason (Optional)"
                  }
                  value={rejectReason}
                  onChange={(e) => setRejectReason(e.target.value)}
                  placeholder={
                    actionDialog.action === "reject"
                      ? "Please provide a detailed reason for rejection..."
                      : actionDialog.action === "suspend"
                      ? "Please provide a reason for suspension (optional)..."
                      : "Please provide a reason for maintenance (optional)..."
                  }
                  required={actionDialog.action === "reject"}
                  disabled={submitting}
                />
              )}
            </MDBox>
          </DialogContent>
          <DialogActions>
            <MDButton
              onClick={() => {
                setActionDialog({ open: false, action: null });
                setRejectReason("");
                setSelectedProperty(null);
              }}
              disabled={submitting}
            >
              Cancel
            </MDButton>
            <MDButton
              onClick={confirmAction}
              variant="gradient"
              color={
                actionDialog.action === "approve"
                  ? "success"
                  : actionDialog.action === "delete"
                  ? "error"
                  : actionDialog.action === "suspend" || actionDialog.action === "maintenance"
                  ? "warning"
                  : "error"
              }
              disabled={submitting || (actionDialog.action === "reject" && !rejectReason.trim())}
            >
              {submitting
                ? "Processing..."
                : actionDialog.action === "approve"
                ? "Approve"
                : actionDialog.action === "reject"
                ? "Reject"
                : actionDialog.action === "suspend"
                ? "Suspend"
                : actionDialog.action === "maintenance"
                ? "Set Maintenance"
                : actionDialog.action === "delete"
                ? "Delete"
                : "Confirm"}
            </MDButton>
          </DialogActions>
        </Dialog>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}

export default PropertiesManagement;
